import { Fragment, ReactNode } from 'react';

import styled, { CSSProperties } from 'styled-components';

import { CardPropsSpaceSplit } from '@components/card/card';
import { IconFancyContainer, IconFancyContainerProps } from '@components/icon-fancy-container';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { Link } from '@components/link';
import { Stack } from '@components/stack';
import { TitleDescription, TitleDescriptionProps } from '@components/title-description/';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { transitionStandard } from '@utils/transitions';
import { MakePropertiesRequired, XOR } from '@utils/types';

import { Card } from '../card';
import { Menu, MenuItem, MenuItemProps, MenuSeparator } from '../menu/menu';

type ActionItem = MakePropertiesRequired<MenuItemProps, 'id'> | 'separator';

/** A card shows either a plain icon or a fancy container — never both. */
type IconSlot = XOR<{ icon?: ReactNode }, { iconContainer?: IconFancyContainerProps }>;

export type ListCardProps = (CardPropsSpaceSplit | { spaceX?: never; spaceY?: never }) &
  Omit<TitleDescriptionProps, 'icon'> &
  IconSlot & {
    actionItems?: ActionItem[];
    actions?: ReactNode;
    /** Rendered below `details`, inside the title column — not full card width. */
    footer?: ReactNode;
    to?: string;
    isActive?: boolean;
    onClick?: () => void;
    styles?: CSSProperties;
    className?: string;
    /** Root only, so Playwright's strict mode finds one match. */
    dataCy?: string;
  };

export const ListCard = ({
  actionItems,
  actions,
  footer,
  spaceX = 6,
  spaceY = 6,
  to = '',
  isActive,
  styles,
  icon,
  iconContainer,
  iconRight,
  details,
  title,
  description,
  titleFont,
  titleColor,
  space,
  disableEllipsis,
  descriptionFont,
  className,
  onClick,
  dataCy,
  ...rest
}: ListCardProps) => {
  const hasLink = to.length > 0;
  const Wrapper = hasLink ? Link : Fragment;
  const wrapperProps = hasLink ? { to } : {};

  return (
    <Wrapper {...wrapperProps}>
      <StyledCard
        className={className}
        data-cy={dataCy}
        interactive={hasLink || Boolean(onClick)}
        spaceX={spaceX}
        spaceY={spaceY}
        isActive={isActive}
        onClick={onClick}
        {...rest}
        style={{
          ...styles,
          ...(onClick ? { cursor: 'pointer' } : {}),
        }}
      >
        <TitleDescription
          title={title}
          description={description}
          titleFont={titleFont}
          titleColor={titleColor}
          space={space}
          disableEllipsis={disableEllipsis}
          descriptionFont={descriptionFont}
          icon={iconContainer ? <IconFancyContainer {...iconContainer} /> : icon}
          details={
            footer ? (
              <>
                {details}
                <FooterSlot>{footer}</FooterSlot>
              </>
            ) : (
              details
            )
          }
        />
        <ActionContainer orientation="horizontal" space={4}>
          {actions}
          {actionItems && (
            <Menu
              buttonProps={{
                iconLeft: <IconMoreVertical />,
                variant: 'ghost',
              }}
              placement="left-start"
            >
              {actionItems.map((item, idx) =>
                item === 'separator' ? (
                  <MenuSeparator key={idx} />
                ) : (
                  <MenuItem key={item.id} {...item} />
                ),
              )}
            </Menu>
          )}
        </ActionContainer>
        {iconRight}
      </StyledCard>
    </Wrapper>
  );
};

const ActionContainer = styled(Stack)({
  minWidth: 'fit-content',
});

/**
 * The footer sits inside TitleDescription's text column so it lines up with the title
 * rather than the icon, and so `actions` stay centred against the card's full height.
 */
const FooterSlot = styled.div({
  marginTop: theme.space[2],
});

const StyledCard = styled(Card)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: pxToRem(16),
  transition: transitionStandard('background', 'border-color', 'box-shadow'),
});
